import { GraphQLClient } from 'graphql-request';
import { gql } from 'urql';
import { MonetaryAmount, Query } from '../generated/graphql';

type ClosedAccountData = {
  id: string;
  name: string;
  ownedValue: MonetaryAmount | null | undefined;
  closedAt?: string | null;
  // The route segment used to open the account detail side panel.
  accountType: string;
};

export const getClosedCashAccounts = async (
  client: GraphQLClient,
  householdID: string
): Promise<ClosedAccountData[]> => {
  const result = await client.request<Pick<Query, 'cashAccounts'>>(
    gql`
      query GetClosedCashAccounts($householdID: ID!) {
        cashAccounts(householdID: $householdID, includeClosedAccounts: true) {
          items {
            id
            name
            cashAccountType
            ownedValue {
              __typename
              value
              currencyCode
            }
            closedAt
          }
        }
      }
    `,
    { householdID }
  );
  return result.cashAccounts.items.map(({ cashAccountType, ...rest }) => ({ ...rest, accountType: cashAccountType }));
};

export const getClosedInvestments = async (
  client: GraphQLClient,
  householdID: string
): Promise<ClosedAccountData[]> => {
  const result = await client.request<Pick<Query, 'investments'>>(
    gql`
      query GetClosedInvestments($householdID: ID!) {
        investments(householdID: $householdID, includeClosedAccounts: true) {
          items {
            id
            name
            investmentType
            ownedValue {
              __typename
              value
              currencyCode
            }
            closedAt
          }
        }
      }
    `,
    { householdID }
  );
  return result.investments.items.map(({ investmentType, ...rest }) => ({ ...rest, accountType: investmentType }));
};

export const getClosedBusinesses = async (
  client: GraphQLClient,
  householdID: string
): Promise<ClosedAccountData[]> => {
  const result = await client.request<Pick<Query, 'businesses'>>(
    gql`
      query GetClosedBusinesses($householdID: ID!) {
        businesses(householdID: $householdID, includeClosedAccounts: true) {
          items {
            id
            name
            ownedValue {
              __typename
              value
              currencyCode
            }
            closedAt
          }
        }
      }
    `,
    { householdID }
  );
  return result.businesses.items.map((item): ClosedAccountData => ({ ...item, accountType: 'businesses' }));
};

export const getClosedRealEstateProperties = async (
  client: GraphQLClient,
  householdID: string
): Promise<ClosedAccountData[]> => {
  const result = await client.request<Pick<Query, 'realEstateProperties'>>(
    gql`
      query GetClosedRealEstateProperties($householdID: ID!) {
        realEstateProperties(householdID: $householdID, includeClosedAccounts: true) {
          items {
            id
            name
            realEstateType
            ownedValue {
              __typename
              value
              currencyCode
            }
            closedAt
          }
        }
      }
    `,
    { householdID }
  );
  return result.realEstateProperties.items.map(({ realEstateType, ...rest }) => ({
    ...rest,
    accountType: realEstateType,
  }));
};

export const getClosedLoans = async (
  client: GraphQLClient,
  householdID: string
): Promise<ClosedAccountData[]> => {
  type LoanItem = {
    id: string;
    name: string;
    loanType: string;
    ownedBalance: MonetaryAmount;
    closedAt?: string | null;
  };
  const result = await client.request<{ loans: { items: LoanItem[] } }>(
    gql`
      query GetClosedLoans($householdID: ID!) {
        loans(householdID: $householdID, includeClosedAccounts: true) {
          items {
            id
            name
            loanType
            ownedBalance {
              __typename
              value
              currencyCode
            }
            closedAt
          }
        }
      }
    `,
    { householdID }
  );
  return result.loans.items.map(({ ownedBalance, loanType, ...rest }) => ({
    ...rest,
    ownedValue: ownedBalance,
    accountType: loanType,
  }));
};

export const getClosedPersonalPropertyItems = async (
  client: GraphQLClient,
  householdID: string
): Promise<ClosedAccountData[]> => {
  const result = await client.request<Pick<Query, 'personalPropertyItems'>>(
    gql`
      query GetClosedPersonalPropertyItems($householdID: ID!) {
        personalPropertyItems(householdID: $householdID, includeClosedAccounts: true) {
          items {
            id
            name
            ownedValue {
              __typename
              value
              currencyCode
            }
            closedAt
          }
        }
      }
    `,
    { householdID }
  );
  return result.personalPropertyItems.items.map(
    (item): ClosedAccountData => ({
      ...item,
      accountType: 'personalPropertyItems',
    })
  );
};

export const getClosedNoteReceivables = async (
  client: GraphQLClient,
  householdID: string
): Promise<ClosedAccountData[]> => {
  const result = await client.request<Pick<Query, 'noteReceivables'>>(
    gql`
      query GetClosedNoteReceivables($householdID: ID!) {
        noteReceivables(householdID: $householdID, includeClosedAccounts: true) {
          items {
            id
            name
            ownedValue {
              __typename
              value
              currencyCode
            }
            closedAt
          }
        }
      }
    `,
    { householdID }
  );
  return result.noteReceivables.items.map(
    (item): ClosedAccountData => ({
      ...item,
      accountType: 'noteReceivables',
    })
  );
};
